import { useState, useEffect, useCallback } from "react";
import { Spinner, Button, Input, Label } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import Flatpickr from "react-flatpickr";
import { fetchUserBalanceHistory } from "../../../slices/users/thunk";
import { getUserBalanceHistory } from "../../../helpers/apis";

// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = (v) => (v !== undefined && v !== null ? Number(v).toLocaleString() : "0");

const fmtTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const dateStr = d.toLocaleDateString("en-GB", { day: '2-digit', month: 'short' });
    const timeStr = d.toLocaleTimeString("en-US", {
        hour: "2-digit", minute: "2-digit", hour12: true
    });
    return `${dateStr} | ${timeStr}`;
};

const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const yesterdayStr = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const last7DaysStr = () => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const thisMonthStartStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const TYPE_META = {
    round_bet: { label: "BET", bg: "#fff0f0", color: "#c62828", border: "#ffcdd2" },
    round_win: { label: "WIN", bg: "#f0fff4", color: "#1b5e20", border: "#c8e6c9" },
    registration: { label: "REGISTER", bg: "#e8f0fe", color: "#1a237e", border: "#c5cae9" },
    admin_add: { label: "DEPOSIT", bg: "#f0fff4", color: "#1b5e20", border: "#c8e6c9" },
    admin_remove: { label: "WITHDRAW", bg: "#fff3e0", color: "#e65100", border: "#ffe0b2" },
    joker_win: { label: "JOKER WIN", bg: "#f3e5f5", color: "#6a1b9a", border: "#e1bee7" },
};

const getTypeMeta = (type) =>
    TYPE_META[type] || { label: (type || "OTHER").toUpperCase(), bg: "#f5f5f5", color: "#455a64", border: "#e0e0e0" };

// ── Stat summary card ─────────────────────────────────────────────────────────
const SummaryCard = ({ icon, label, value, valueColor, bg }) => (
    <div style={{
        background: bg || "#fff",
        border: "1.5px solid #e8eaf6",
        borderRadius: 14,
        padding: "12px 18px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        flex: 1,
        minWidth: 100
    }}>
        <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: valueColor + "18",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
        }}>
            <i className={icon} style={{ fontSize: 18, color: valueColor }} />
        </div>
        <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#90a4ae", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {label}
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color: valueColor, lineHeight: 1.1, marginTop: 2 }}>
                {value}
            </div>
        </div>
    </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const BalanceHistoryTab = ({ userId, gameType = "lt" }) => {
    const dispatch = useDispatch();
    const { userBalanceHistory, balanceHistoryLoading } = useSelector((s) => ({
        userBalanceHistory: s.Users.userBalanceHistory,
        balanceHistoryLoading: s.Users.balanceHistoryLoading,
    }));

    const [startDate, setStartDate] = useState(todayStr());
    const [endDate, setEndDate] = useState(todayStr());
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [isExporting, setIsExporting] = useState(false);

    const balanceData = userId ? userBalanceHistory[userId] || null : null;
    const history = balanceData?.history || [];
    const pagination = balanceData?.pagination || {};
    const summary = balanceData?.summary || {};
    const userInfo = balanceData?.userInfo || {};

    // Use opening/closing balance directly from the API summary
    const openingBalance = summary.openingBalance ?? 0;
    const closingBalance = summary.closingBalance ?? 0;
    const netProfit = summary.netProfit ?? 0;
    const isProfit = netProfit >= 0;

    const load = useCallback((p = 1, start = startDate, end = endDate, lim = limit) => {
        if (!userId) return;
        dispatch(fetchUserBalanceHistory(userId, { gameType, startDate: start, endDate: end, page: p, limit: lim }));
    }, [userId, gameType, startDate, endDate, limit, dispatch]);

    useEffect(() => { load(1, startDate, endDate, limit); }, [startDate, endDate, userId]);

    const handlePage = (p) => { setPage(p); load(p, startDate, endDate, limit); };

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1);
        load(1, startDate, endDate, newLimit);
    };

    const handleExport = async () => {
        if (!userId) return;
        setIsExporting(true);
        try {
            // Call API directly for export to avoid touching/clearing Redux state used for UI
            const res = await getUserBalanceHistory(userId, {
                gameType,
                startDate,
                endDate,
                export: 'excel'
            });

            if (res && res.status === 1 && res.data) {
                const { exportData, filename, summary: exportSummary, columns: exportColumns } = res.data;

                // Create Summary Data for Excel
                const wsData = [
                    [`BALANCE HISTORY REPORT (${startDate} to ${endDate})`],
                    ["Account Number", userInfo?.acnumber || userInfo?.username || userId],
                    ["Day Opening", exportSummary?.openingBalance || 0],
                    ["Day Closing", exportSummary?.closingBalance || 0],
                    ["Net P&L", exportSummary?.netProfit || 0],
                    ["Total Won", exportSummary?.totalWonAmount || 0],
                    ["Total Lost", exportSummary?.totalLostAmount || 0],
                    [], // Blank Row
                ];

                // Add Table Headers
                if (exportColumns && Array.isArray(exportColumns)) {
                    wsData.push(exportColumns.map(c => c.header));
                    exportData.forEach(item => {
                        wsData.push(exportColumns.map(c => item[c.key] ?? ''));
                    });
                } else if (exportData.length > 0) {
                    wsData.push(Object.keys(exportData[0]));
                    exportData.forEach(item => wsData.push(Object.values(item)));
                }

                const ws = XLSX.utils.aoa_to_sheet(wsData);
                if (exportColumns && Array.isArray(exportColumns)) {
                    ws['!cols'] = exportColumns.map(c => ({ wch: c.width || 18 }));
                }

                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Balance History");
                XLSX.writeFile(wb, filename || `balance-history-${userId}.xlsx`);
            }
        } catch (err) {
            console.error("Export error", err);
        } finally {
            setIsExporting(false);
        }
    };

    // ── Section: Balance Banner Column definitions mirror ─────────────────────
    // ── Table Column Definitions ─────────────────────────────────────────────
    const colWidths = ["12%", "12%", "34%", "12%", "15%", "15%"];
    const headers = ["Time", "Activity", "Description", "Round #", "Amount (+/-)", "Final Balance"];

    return (
        <div className="p-2">
            {/* ── Date Filter ── */}
            <div style={{
                background: "linear-gradient(90deg, #ffffff 0%, #eef2ff 100%)",
                borderRadius: 14, padding: "14px 18px",
                display: "flex", alignItems: "flex-end", gap: 12, justifyContent: "space-between",
                marginBottom: 16, border: "1.5px solid #e8eaf6",
                flexWrap: "wrap"
            }}>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
                    <div style={{ flex: "0 0 240px" }}>
                        <Label style={{
                            fontSize: 10, fontWeight: 800, color: "#5c6bc0",
                            textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 5,
                            display: "flex", alignItems: "center", gap: 5
                        }}>
                            <i className="ri-calendar-range-line" style={{ fontSize: 13 }} /> Select Date Range
                        </Label>
                        <Flatpickr
                            className="form-control"
                            options={{
                                mode: "range",
                                dateFormat: "Y-m-d",
                                maxDate: todayStr(),
                            }}
                            value={[startDate, endDate]}
                            onChange={(dates) => {
                                if (dates.length === 2) {
                                    const formatDateLocal = (d) => {
                                        const year = d.getFullYear();
                                        const month = String(d.getMonth() + 1).padStart(2, "0");
                                        const day = String(d.getDate()).padStart(2, "0");
                                        return `${year}-${month}-${day}`;
                                    };
                                    setStartDate(formatDateLocal(dates[0]));
                                    setEndDate(formatDateLocal(dates[1]));
                                    setPage(1);
                                }
                            }}
                            style={{
                                border: "1.5px solid #c5cae9", borderRadius: 5,
                                fontWeight: 700, fontSize: 13, color: "#283593",
                                padding: "7px 12px", background: "#fff"
                            }}
                        />
                    </div>

                    {/* ── Presets Dropdown ── */}
                    <div style={{ display: "flex", flexDirection: "column", minWidth: 150 }}>
                        <Label style={{
                            fontSize: 10, fontWeight: 800, color: "#5c6bc0",
                            textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 5,
                            display: "flex", alignItems: "center", gap: 5
                        }}>
                            <i className="ri-time-line" style={{ fontSize: 13 }} /> Quick Select
                        </Label>
                        <select
                            className="form-select"
                            style={{
                                border: "1.5px solid #c5cae9", borderRadius: 5,
                                fontWeight: 700, fontSize: 13, color: "#283593",
                                padding: "7px 32px 7px 12px", background: "#fff", cursor: "pointer",
                                appearance: "none", WebkitAppearance: "none",
                                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23283593' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e")`,
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right .75rem center",
                                backgroundSize: "16px 12px",
                                height: "35px"
                            }}
                            value={
                                startDate === todayStr() && endDate === todayStr() ? "Today" :
                                    startDate === yesterdayStr() && endDate === yesterdayStr() ? "Yesterday" :
                                        startDate === last7DaysStr() && endDate === todayStr() ? "7 Days" :
                                            startDate === thisMonthStartStr() && endDate === todayStr() ? "This Month" : "Custom"
                            }
                            onChange={(e) => {
                                const val = e.target.value;
                                let start = startDate, end = endDate;
                                if (val === "Today") { start = todayStr(); end = todayStr(); }
                                else if (val === "Yesterday") { start = yesterdayStr(); end = yesterdayStr(); }
                                else if (val === "7 Days") { start = last7DaysStr(); end = todayStr(); }
                                else if (val === "This Month") { start = thisMonthStartStr(); end = todayStr(); }
                                else { return; }

                                setStartDate(start);
                                setEndDate(end);
                                setPage(1);
                                load(1, start, end, limit);
                            }}
                            disabled={balanceHistoryLoading}
                        >
                            <option value="Custom">Custom Range</option>
                            <option value="Today">Today</option>
                            <option value="Yesterday">Yesterday</option>
                            <option value="7 Days">Last 7 Days</option>
                            <option value="This Month">This Month</option>
                        </select>
                    </div>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                    <Button
                        color="primary"
                        size="sm"
                        onClick={() => load(1, startDate, endDate)}
                        style={{ borderRadius: 5, fontWeight: 700, padding: "8px 18px" }}
                        disabled={balanceHistoryLoading}
                    >
                        <i className="ri-filter-3-line me-1" />
                        Apply Filter
                    </Button>

                    <Button
                        color="primary"
                        size="sm"
                        onClick={handleExport}
                        style={{ borderRadius: 5, fontWeight: 700, padding: "8px 18px" }}
                        disabled={balanceHistoryLoading || isExporting}
                    >
                        {isExporting ? (
                            <><i className="bx bx-loader-alt bx-spin me-1" /> Exporting...</>
                        ) : (
                            <><i className="ri-file-excel-2-line me-1" /> Export</>
                        )}
                    </Button>
                </div>
            </div>

            {/* ── Loading ── */}
            {balanceHistoryLoading && (
                <div className="text-center py-5">
                    <Spinner color="primary" style={{ width: "2.5rem", height: "2.5rem" }} />
                    <p className="text-muted mt-3 mb-0" style={{ fontWeight: 600 }}>Loading balance history…</p>
                </div>
            )}

            {/* ── No data ── */}
            {!balanceHistoryLoading && history.length === 0 && (
                <div className="text-center py-5">
                    <div style={{
                        width: 70, height: 70, borderRadius: "50%",
                        background: "#e8eaf6", margin: "0 auto 16px",
                        display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                        <i className="ri-inbox-line" style={{ fontSize: 34, color: "#9fa8da" }} />
                    </div>
                    <p className="mb-0" style={{ color: "#78909c", fontWeight: 600 }}>No balance history found for the selected date.</p>
                </div>
            )}

            {/* ── Content ── */}
            {!balanceHistoryLoading && history.length > 0 && (<>

                {/* Summary Flow Section */}
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: 12,
                    marginBottom: 20
                }}>
                    <SummaryCard
                        icon="ri-wallet-3-line"
                        label="Day Start"
                        value={fmt(openingBalance)}
                        valueColor="#3f51b5"
                        bg="linear-gradient(to left, #eef2ff 0%, #ffffff 100%)"
                    />
                    <SummaryCard
                        icon="ri-add-circle-line"
                        label="Total Won"
                        value={fmt(summary.totalWonAmount ?? 0)}
                        valueColor="#1b7754"
                        bg="linear-gradient(to left, #f0fff4 0%, #ffffff 100%)"
                    />
                    <SummaryCard
                        icon="ri-indeterminate-circle-line"
                        label="Total Lost"
                        value={fmt(Math.abs(summary.totalLostAmount ?? 0))}
                        valueColor="#c62828"
                        bg="linear-gradient(to left, #fff5f5 0%, #ffffff 100%)"
                    />
                    <SummaryCard
                        icon="ri-funds-line"
                        label="Net P&L"
                        value={(isProfit ? "+" : "") + fmt(netProfit)}
                        valueColor={isProfit ? "#1b7754" : "#c62828"}
                        bg={isProfit ? "linear-gradient(to left, #f0fff4 0%, #ffffff 100%)" : "linear-gradient(to left, #fff5f5 0%, #ffffff 100%)"}
                    />
                    <SummaryCard
                        icon="ri-bank-line"
                        label="Day End"
                        value={fmt(closingBalance)}
                        valueColor="#263238"
                        bg="linear-gradient(135deg, #fff 0%, #f1f8e9 100%)"
                    />
                </div>

                <div style={{
                    fontSize: 14, fontWeight: 800, color: "#1a237e",
                    marginBottom: 16, display: "flex", alignItems: "center", gap: 8,
                    padding: "0 4px", letterSpacing: "0.5px"
                }}>
                    <div style={{ width: 4, height: 18, background: "#3f51b5", borderRadius: 4 }} />
                    ACTIVITY TIMELINE
                </div>

                {/* ── Timeline Container ── */}
                <div style={{ position: "relative", paddingLeft: 20 }}>
                    {/* Vertical connecting line */}
                    <div style={{
                        position: "absolute", left: 35, top: 20, bottom: 20,
                        width: 2, background: "linear-gradient(to bottom, #3f51b5, #e0e0e0)",
                        zIndex: 0, opacity: 0.3
                    }} />

                    {/* ══ START MILESTONE ══ */}
                    <div style={{
                        position: "relative", zIndex: 1, marginBottom: 16,
                        background: "#fff", border: "1.5px solid #e8eaf6", borderRadius: 12,
                        padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.02)"
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                                width: 28, height: 28, borderRadius: "50%", background: "#3f51b5",
                                display: "flex", alignItems: "center", justifyContent: "center"
                            }}>
                                <i className="ri-flag-2-fill" style={{ color: "#fff", fontSize: 13 }} />
                            </div>
                            <div>
                                <div style={{ fontSize: 11, fontWeight: 800, color: "#1a237e" }}>DAY OPENING</div>
                                <div style={{ fontSize: 10, color: "#90a4ae", fontWeight: 600 }}>Balance at 12:00 AM</div>
                            </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 16, fontWeight: 900, color: "#3f51b5" }}>{fmt(openingBalance)}</div>
                            <div style={{ fontSize: 8, fontWeight: 700, color: "#9fa8da", textTransform: "uppercase" }}>Starting Amount</div>
                        </div>
                    </div>

                    {/* ══ TRANSACTIONS ══ */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        {history.map((row, idx) => {
                            const tm = getTypeMeta(row.type);
                            const chg = row.changeAmount ?? 0;
                            const isPositive = chg >= 0;

                            return (
                                <div key={row._id || idx} style={{
                                    position: "relative", zIndex: 1,
                                    background: "#fff", border: "1px solid #f0f0f0", borderRadius: 12,
                                    padding: "12px 16px", display: "flex", alignItems: "center", gap: 14,
                                    transition: "transform 0.2s, box-shadow 0.2s",
                                    cursor: "pointer"
                                }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.transform = "translateX(5px)";
                                        e.currentTarget.style.boxShadow = "4px 4px 15px rgba(0,0,0,0.05)";
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.transform = "translateX(0)";
                                        e.currentTarget.style.boxShadow = "none";
                                    }}
                                >
                                    {/* Icon */}
                                    <div style={{
                                        position: "relative",
                                        width: 30, height: 30, borderRadius: "50%",
                                        background: tm.bg, border: `1.5px solid ${tm.border}`,
                                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                                    }}>
                                        <i className={isPositive ? "ri-arrow-up-line" : "ri-arrow-down-line"} style={{ color: tm.color, fontSize: 14 }} />
                                        {row.isJokerRound && row.gameSource === 'lt' && (
                                            <img
                                                src="/joker.png"
                                                alt="Joker"
                                                style={{
                                                    position: 'absolute',
                                                    top: -20,
                                                    left: -25,
                                                    width: 40,
                                                    height: 40,
                                                    objectFit: 'contain',
                                                    transform: 'rotate(20deg)',
                                                    zIndex: 2
                                                }}
                                            />
                                        )}
                                    </div>

                                    {/* Details */}
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                            <span style={{ fontSize: 11, fontWeight: 700, color: "#78909c", minWidth: 110, whiteSpace: "nowrap" }}>
                                                {fmtTime(row.createdAt)}
                                            </span>
                                            <span style={{ fontSize: 12, fontWeight: 800, color: "#37474f" }}>{tm.label}</span>
                                            {row.roundNumber && (
                                                <span style={{ fontSize: 10, fontWeight: 700, color: "#5c6bc0", background: "#e8eaf6", padding: "1px 6px", borderRadius: 4 }}>
                                                    #{row.roundNumber}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: 11, color: "#78909c", marginTop: 2, maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {row.description || "No description provided"}
                                        </div>
                                    </div>

                                    {/* Financials */}
                                    <div style={{ textAlign: "right", minWidth: 100 }}>
                                        <div style={{ fontSize: 14, fontWeight: 900, color: isPositive ? "#2e7d32" : "#d32f2f" }}>
                                            {isPositive ? "+" : "−"}{fmt(Math.abs(chg))}
                                        </div>
                                        <div style={{ fontSize: 10, fontWeight: 700, color: "#90a4ae" }}>
                                            Bal: {fmt(row.closingBalance ?? 0)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* ══ END MILESTONE ══ */}
                    <div style={{
                        position: "relative", zIndex: 1, marginTop: 16,
                        background: isProfit ? "#f1f8e9" : "#fff5f5",
                        border: `1.5px dashed ${isProfit ? "#c8e6c9" : "#ffcdd2"}`,
                        borderRadius: 12,
                        padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between"
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                                width: 32, height: 32, borderRadius: "50%", background: isProfit ? "#2e7d32" : "#c62828",
                                display: "flex", alignItems: "center", justifyContent: "center"
                            }}>
                                <i className={isProfit ? "ri-trophy-fill" : "ri-close-fill"} style={{ color: "#fff", fontSize: 15 }} />
                            </div>
                            <div>
                                <div style={{ fontSize: 11, fontWeight: 800, color: isProfit ? "#1b5e20" : "#b71c1c" }}>
                                    {isProfit ? "PROFITABLE DAY" : "DAY ENDED IN LOSS"}
                                </div>
                                <div style={{ fontSize: 10, color: isProfit ? "#4caf50" : "#e57373", fontWeight: 700 }}>
                                    Net: {isProfit ? "+" : ""}{fmt(netProfit)} Charges
                                </div>
                            </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 20, fontWeight: 950, color: isProfit ? "#1b5e20" : "#b71c1c" }}>{fmt(closingBalance)}</div>
                            <div style={{ fontSize: 8, fontWeight: 800, color: isProfit ? "#81c784" : "#ef9a9a", textTransform: "uppercase" }}>Final Closing Balance</div>
                        </div>
                    </div>
                </div>

                {/* ── Pagination ── */}
                <div style={{
                    display: "flex", alignItems: "center", justifyContent: "flex-end",
                    gap: 16, marginTop: 14, flexWrap: "wrap"
                }}>
                    {/* Rows per page */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 12, color: "#6c757d", fontWeight: 500, whiteSpace: "nowrap" }}>Rows per page:</span>
                        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                            <select
                                value={limit}
                                onChange={(e) => handleLimitChange(Number(e.target.value))}
                                style={{
                                    border: "none", borderRadius: 0,
                                    padding: "4px 20px 4px 8px", fontSize: 12, fontWeight: 500,
                                    color: "#495057", background: "transparent", cursor: "pointer",
                                    outline: "none", appearance: "none", WebkitAppearance: "none"
                                }}
                            >
                                {[10, 15, 20, 25, 30].map(n => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                            <i className="ri-arrow-down-s-fill" style={{
                                position: "absolute", right: 4, top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: 14, color: "#6c757d", pointerEvents: "none"
                            }} />
                        </div>
                    </div>

                    {/* Record count */}
                    <span style={{ fontSize: 12, color: "#6c757d", fontWeight: 500, whiteSpace: "nowrap" }}>
                        {pagination.totalRecords > 0
                            ? `${((page - 1) * limit) + 1}–${Math.min(page * limit, pagination.totalRecords)} of ${pagination.totalRecords}`
                            : "0 of 0"}
                    </span>

                    {/* Navigation buttons */}
                    <div style={{
                        display: "flex", alignItems: "center",
                        overflow: "hidden"
                    }}>
                        {/* First */}
                        <button
                            onClick={() => handlePage(1)}
                            disabled={!pagination.hasPrev}
                            style={{
                                border: "none",
                                background: pagination.hasPrev ? "#fff" : "#f8f9fa",
                                color: pagination.hasPrev ? "#495057" : "#adb5bd",
                                padding: "6px 10px", fontSize: 16, fontWeight: 900,
                                cursor: pagination.hasPrev ? "pointer" : "not-allowed",
                                lineHeight: 1, display: "flex", alignItems: "center"
                            }}
                        >
                            <i className="ri-skip-left-line" />
                        </button>
                        {/* Prev */}
                        <button
                            onClick={() => handlePage(page - 1)}
                            disabled={!pagination.hasPrev}
                            style={{
                                border: "none",
                                background: pagination.hasPrev ? "#fff" : "#f8f9fa",
                                color: pagination.hasPrev ? "#495057" : "#adb5bd",
                                padding: "6px 10px", fontSize: 16, fontWeight: 900,
                                cursor: pagination.hasPrev ? "pointer" : "not-allowed",
                                lineHeight: 1, display: "flex", alignItems: "center"
                            }}
                        >
                            <i className="ri-arrow-left-s-line" />
                        </button>
                        {/* Next */}
                        <button
                            onClick={() => handlePage(page + 1)}
                            disabled={!pagination.hasNext}
                            style={{
                                border: "none",
                                background: pagination.hasNext ? "#fff" : "#f8f9fa",
                                color: pagination.hasNext ? "#495057" : "#adb5bd",
                                padding: "6px 10px", fontSize: 16, fontWeight: 900,
                                cursor: pagination.hasNext ? "pointer" : "not-allowed",
                                lineHeight: 1, display: "flex", alignItems: "center"
                            }}
                        >
                            <i className="ri-arrow-right-s-line" />
                        </button>
                        {/* Last */}
                        <button
                            onClick={() => handlePage(pagination.totalPages || 1)}
                            disabled={!pagination.hasNext}
                            style={{
                                border: "none",
                                background: pagination.hasNext ? "#fff" : "#f8f9fa",
                                color: pagination.hasNext ? "#495057" : "#adb5bd",
                                padding: "6px 10px", fontSize: 16, fontWeight: 900,
                                cursor: pagination.hasNext ? "pointer" : "not-allowed",
                                lineHeight: 1, display: "flex", alignItems: "center"
                            }}
                        >
                            <i className="ri-skip-right-line" />
                        </button>
                    </div>
                </div>
            </>)}
        </div>
    );
};

export default BalanceHistoryTab;
